import { useField } from "formik";
import { forwardRef, type ReactNode } from "react";
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from "@/components/common/rich-text-editor";
import { Label } from "@/components/ui/label";

interface RichTextFieldProps {
  name: string;
  label: string;
  /** Extra element next to the label, e.g. a merge-token bar. */
  labelAdornment?: ReactNode;
  /** Override the label's classes (uppercase micro-label on data-entry grids). */
  labelClassName?: string;
  /** Help text rendered under the editor (e.g. placeholder-token hints). */
  hint?: ReactNode;
  onFocus?: () => void;
  minHeight?: number;
  disabled?: boolean;
}

/**
 * Formik-bound rich-text body field: Label + RichTextEditor + validation error,
 * mirroring FormField's layout so WYSIWYG bodies render errors consistently.
 * The forwarded ref exposes `insertText` for merge-token toolbars.
 */
export const RichTextField = forwardRef<RichTextEditorHandle, RichTextFieldProps>(
  function RichTextField(
    { name, label, labelAdornment, labelClassName, hint, onFocus, minHeight, disabled },
    ref,
  ) {
    const [field, meta, helpers] = useField<string>(name);
    const hasError = meta.touched && !!meta.error;

    return (
      <div className="grid gap-2">
        <div className="flex items-center justify-between">
          <Label htmlFor={name} className={labelClassName}>
            {label}
          </Label>
          {labelAdornment}
        </div>
        <RichTextEditor
          ref={ref}
          id={name}
          ariaLabel={label}
          ariaInvalid={hasError}
          value={field.value}
          onChange={(html) => {
            helpers.setValue(html);
            if (!meta.touched) helpers.setTouched(true, false);
          }}
          onFocus={onFocus}
          minHeight={minHeight}
          disabled={disabled}
        />
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
      </div>
    );
  },
);
